import React, { useState, useMemo } from "react";
import "./AllProduct.css";

import Row from "../Routes/Components/Row";
import Navbar from "./Components/Navbar";
import Header from "../Routes/Components/Header";
import ProductCard from "../Components/ProductCard";
import Footer from "../Components/Footer";

import { motion, AnimatePresence } from 'framer-motion'
import CustomizedCake from "../Components/CustomizedCake";
import { useCatalog } from "../Components/CatalogContext";
import { SORT_OPTIONS, availableBuckets, applyCatalogFilters } from "../data/catalogFilters";
import "./Components/CatalogFilters.css";

const PRODUCT_PAGE_BANNER_IMAGE = "/bgimage.jpeg";

const PRODUCT_PAGE_TEXT = {
  heading: "Crafted for Wellness. Made with Love.",
  body: "Every product in our range is rooted in ancient Ayurvedic wisdom and crafted with the finest natural ingredients. From our signature chai blends to lotus seed snacks and Berry Banger wellness shots - each offering is designed to nourish your body, calm your mind, and connect you to something deeper. No artificial flavours. No shortcuts. Just real food, real healing, and real love.",
};

const CATEGORY_RIBBON_META = {
  'chai': {
    collection: 'The Soul Collection',
    description: 'Brewed karak chai, Sticky Soul concentrate balls, and the Chai Pe Charcha monthly box. Gut-friendly spices, authentic family recipe from Mumbai. Just add hot water - instant warmth.',
  },
  'snacks': {
    collection: 'The Crunch Collection',
    description: 'Roasted lotus seeds (Pop & Bloom), protein trail mixes, brain-boosting and gut-friendly snacks. Ancient crunch, modern munch. High protein, gluten-free, no guilt.',
  },
  'drinks': {
    collection: 'The Refresh Collection',
    description: 'Berry Banger superfruit wellness shots - mixed berries, pomegranate, ginger and a splash of lime. No added sugar, no artificial anything. Antioxidant-rich vitality in a bottle.',
  },
  'subscription boxes': {
    collection: 'The Ritual Collection',
    description: 'The Weekly Ritual and monthly Discovery Box - curated chai, rotating snacks and wellness drinks delivered to your door. Fresh self-care in a box, without the effort.',
  },
  'retail & merch': {
    collection: 'The Lifestyle Collection',
    description: 'Organic cotton aprons, embroidered caps, stainless steel tiffins, masala dabbas, curated gift sets and hands-on chai workshops. Wellness as a lifestyle, made to last.',
  },
};

function AllProduct() {
  const { categories, topProducts } = useCatalog();
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [priceBucket, setPriceBucket] = useState("all");
  const loading = false;
  const error = null;

  // Helper function to get default variant based on category
  const getDefaultVariant = (categoryName) => {
    const category = categoryName?.toLowerCase() || '';
    if (category.includes('cake')) {
      return { weight: '1kg', price: 800 };
    } else if (category.includes('cookie')) {
      return { weight: 'Box of 6', price: 400 };
    }
    return { weight: '1kg', price: 800 };
  };

  // Defensive: Transform top products data for each category, skipping nulls and broken references
  const getTopProductsForCategory = (categoryId) => {
    const topProduct = topProducts.find(tp => tp.category && tp.category._id === categoryId);
    if (!topProduct) return []; // No topProduct for this category

    return [
      topProduct.Product1,
      topProduct.Product2,
      topProduct.Product3,
    ]
    .filter(Boolean) // Filter out any null/undefined products
    .map(product => {
      // If category info is not embedded, use fallback
      const defaultVariant = getDefaultVariant(product?.Category?.Name || product?.category?.Name || "");
      return {
        id: product._id,
        name: product.productName,
        tagline: product.Tagline,
        description: product.ProductDesc,
        price:
          (product.variants && product.variants.length > 0 && product.variants[0].price) ||
          product.Price ||
          defaultVariant.price,
        weight:
          (product.variants && product.variants.length > 0 && product.variants[0].weight) ||
          product.Weight ||
          defaultVariant.weight,
        image: (product.Images && product.Images[0]) || '',
        ingredients: product.Ingredients,
        issoldout: product.isSoldOut
      };
    });
  };

  // Get category name in lowercase for routing
  const getCategoryRoute = (categoryName) => {
    return (categoryName || '').toLowerCase();
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.5rem'
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.5rem',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  const filteredCategories = categories.filter(Boolean).filter(cat => {
    if (activeFilter === "all") return true;
    return cat._id === activeFilter;
  });

  // Price buckets available across the currently selected categories.
  const allShownProducts = filteredCategories.flatMap(c => (c && c._id ? getTopProductsForCategory(c._id) : []));
  const priceBuckets = availableBuckets(allShownProducts);

  return (
    <>
      <Navbar/>
      <div style={{marginTop:'5rem'}} className="all-product-main-container">

        {/* Banner with heading + intro copy */}
        <div className="products-top-banner" style={{ backgroundImage: `url(${PRODUCT_PAGE_BANNER_IMAGE})` }}>
          <div className="products-top-banner-overlay" />
          <div className="products-top-banner-content">
            <p className="products-top-banner-text">Fresh · Handcrafted · Wellness</p>
            <h1 className="products-top-banner-heading">Crafted for Wellness.<br />Made with Love.</h1>
            <p className="products-top-banner-body">{PRODUCT_PAGE_TEXT.body}</p>
          </div>
        </div>

        {/* Category Filter Bar */}
        <div className="products-filter-bar">
          <button
            className={`filter-btn ${activeFilter === "all" ? "filter-btn--active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All
          </button>
          {categories.filter(Boolean).map(cat => (
            <button
              key={cat._id}
              className={`filter-btn ${activeFilter === cat._id ? "filter-btn--active" : ""}`}
              onClick={() => setActiveFilter(cat._id)}
            >
              {cat.Name}
            </button>
          ))}
        </div>

        {/* Price / sort / availability filter toolbar */}
        <div className="cf-bar">
          <div className="cf-group">
            <span className="cf-label">Price</span>
            <button className={`cf-chip ${priceBucket === 'all' ? 'cf-chip--active' : ''}`} onClick={() => setPriceBucket('all')}>All</button>
            {priceBuckets.map((b) => (
              <button key={b.id} className={`cf-chip ${priceBucket === b.id ? 'cf-chip--active' : ''}`} onClick={() => setPriceBucket(b.id)}>{b.label}</button>
            ))}
          </div>
          <span className="cf-spacer" />
          <div className="cf-group">
            <label className="cf-label" htmlFor="ap-sort">Sort</label>
            <select id="ap-sort" className="cf-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {filteredCategories.map((category) => {
          if (!category || !category._id) return null;
          const categoryProducts = applyCatalogFilters(
            getTopProductsForCategory(category._id),
            { sortBy, priceBucket }
          );
          if (categoryProducts.length === 0) return null;
          const categoryRoute = getCategoryRoute(category.Name);

          const ribbonMeta = CATEGORY_RIBBON_META[category.Name?.toLowerCase()] || {};
          // Admin-entered values win; fall back to the curated map, then the raw category name.
          const ribbonTitle = category.CollectionTitle || ribbonMeta.collection || category.Name;
          const ribbonSubtext = category.Description || ribbonMeta.description;

          return (
            <div key={category._id}>
              <div className="category-ribbon">
                <p className="category-ribbon-collection">{ribbonTitle}</p>
                {ribbonSubtext && (
                  <p className="category-ribbon-text">{ribbonSubtext}</p>
                )}
              </div>
              <Row
                title=""
                subtitle=""
                products={categoryProducts}
                buttonText={`Explore More ${category.Name}`}
                category={categoryRoute}
              />
            </div>
          );
        })}

        <div>
          <CustomizedCake />
        </div>

      </div>
      <Footer />
    </>
  );
}

export default AllProduct;
